import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeUrl, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SessionService } from 'src/app/services/session-service';
import { guidePath, guideReaderPath, MarketGuidesService, ReaderManifest } from '../market-guides.service';

/**
 * /market-guides/{slug}/read: the entitled reader. Page images stream from
 * the API (never a public URL). One page at a time on phones, two-page
 * spreads on wide screens, next/previous by button, keyboard or swipe, and a
 * zoom that loads the high-resolution page. Reading position is saved to the
 * account so returning readers pick up where they stopped.
 */
@Component({
  selector: 'pla-guide-reader',
  templateUrl: './guide-reader.component.html',
  styleUrls: ['./guide-reader.component.scss'],
})
export class GuideReaderComponent implements OnInit, OnDestroy {
  public slug = '';
  public manifest: ReaderManifest | null = null;
  public state: 'loading' | 'ready' | 'signin' | 'denied' | 'error' = 'loading';
  public page = 1; // first page on screen
  public zoom = false;
  public spread = false;
  public contentsOpen = false;
  public readonly landingPath = (s: string) => guidePath(s);
  public contents: { page: number; title: string }[] = [];

  private urls = new Map<string, string>();
  private safe = new Map<string, SafeUrl>();
  private pending = new Set<string>();
  private progress$ = new Subject<number>();
  private subs: Subscription[] = [];
  private touchX: number | null = null;
  private touchY: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private guides: MarketGuidesService,
    private session: SessionService,
    private title: Title,
    private location: Location,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.slug = this.route.snapshot.paramMap.get('slug') || '';
    this.spread = this.wide();
    document.body.style.overflow = 'hidden';
    document.body.classList.add('pla-reader-open');
    if (!this.session.isLoggedIn()) {
      this.state = 'signin';
      return;
    }
    this.subs.push(this.progress$.pipe(debounceTime(1200)).subscribe((p) => this.guides.progress(this.slug, p).subscribe({ error: () => {} })));
    this.subs.push(this.guides.get(this.slug).subscribe({ next: (p) => (this.contents = p.landing?.contents || []), error: () => {} }));
    this.subs.push(
      this.guides.reader(this.slug).subscribe({
        next: (m) => {
          this.manifest = m;
          this.state = 'ready';
          this.title.setTitle(`${m.title} | PlateX Market Guides`);
          const q = Number(this.route.snapshot.queryParamMap.get('page'));
          this.go(q >= 1 && q <= m.pageCount ? q : m.lastPage && m.lastPage > 1 ? m.lastPage : 1, false);
        },
        error: (e) => (this.state = e?.status === 401 ? 'signin' : e?.status === 403 || e?.status === 404 ? 'denied' : 'error'),
      })
    );
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    document.body.classList.remove('pla-reader-open');
    this.subs.forEach((s) => s.unsubscribe());
    this.urls.forEach((u) => URL.revokeObjectURL(u));
  }

  // ------------------------------------------------------------ layout

  private wide(): boolean {
    return typeof window !== 'undefined' && window.innerWidth >= 1000 && window.innerWidth > window.innerHeight;
  }

  @HostListener('window:resize')
  onResize(): void {
    const next = this.wide();
    if (next !== this.spread) {
      this.spread = next;
      this.go(this.page, false);
    }
  }

  get count(): number {
    return this.manifest?.pageCount || 0;
  }

  /** Pages on screen: the cover and last page stand alone; spreads pair 2-3, 4-5, ... */
  get visible(): number[] {
    if (!this.spread || this.zoom) return [this.page];
    if (this.page === 1 || this.page === this.count) return [this.page];
    const left = this.page % 2 === 0 ? this.page : this.page - 1;
    return left + 1 <= this.count ? [left, left + 1] : [left];
  }

  get label(): string {
    const v = this.visible;
    return v.length > 1 ? `${v[0]}-${v[1]} of ${this.count}` : `${v[0]} of ${this.count}`;
  }

  get atStart(): boolean {
    return this.visible[0] <= 1;
  }

  get atEnd(): boolean {
    return this.visible[this.visible.length - 1] >= this.count;
  }

  // ------------------------------------------------------------ navigation

  go(p: number, _record = true): void {
    if (!this.manifest) return;
    this.page = Math.min(Math.max(1, p), this.count);
    if (this.spread && !this.zoom && this.page > 1 && this.page < this.count && this.page % 2 === 1) this.page -= 1;
    const v = this.visible;
    v.forEach((n) => this.load(n));
    // Preload what comes next (and one back) so turning feels immediate.
    [v[v.length - 1] + 1, v[v.length - 1] + 2, v[0] - 1].forEach((n) => n >= 1 && n <= this.count && this.load(n));
    if (this.zoom) this.load(this.page, 'l');
    // replaceState, not router.navigate: the app never reuses routes, so a
    // navigation would rebuild the reader on every page turn.
    this.location.replaceState(guideReaderPath(this.slug), `page=${this.page}`);
    this.progress$.next(v[v.length - 1]);
  }

  next(): void {
    if (this.atEnd) return;
    this.go(this.visible[this.visible.length - 1] + 1);
  }

  prev(): void {
    if (this.atStart) return;
    const first = this.visible[0];
    this.go(this.spread && !this.zoom && first > 2 ? first - 2 : first - 1);
  }

  toggleZoom(): void {
    this.zoom = !this.zoom;
    this.go(this.page, false);
  }

  jump(p: number): void {
    this.contentsOpen = false;
    this.go(p);
  }

  @HostListener('document:keydown', ['$event'])
  onKey(e: KeyboardEvent): void {
    if (this.state !== 'ready') return;
    if (e.key === 'ArrowRight' || e.key === 'PageDown') this.next();
    else if (e.key === 'ArrowLeft' || e.key === 'PageUp') this.prev();
    else if (e.key === 'Escape' && (this.zoom || this.contentsOpen)) {
      this.zoom = false;
      this.contentsOpen = false;
      this.go(this.page, false);
    } else return;
    e.preventDefault();
  }

  touchStart(e: TouchEvent): void {
    if (this.zoom || e.touches.length > 1) {
      this.touchX = null;
      return;
    }
    this.touchX = e.touches[0].clientX;
    this.touchY = e.touches[0].clientY;
  }

  touchEnd(e: TouchEvent): void {
    if (this.touchX == null) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - this.touchX;
    const dy = t.clientY - (this.touchY ?? t.clientY);
    this.touchX = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) dx < 0 ? this.next() : this.prev();
  }

  // ------------------------------------------------------------ images

  src(p: number): SafeUrl | null {
    return (this.zoom && this.safe.get(`${p}:l`)) || this.safe.get(`${p}:m`) || null;
  }

  ratio(p: number): string {
    const pg = this.manifest?.pages?.[p - 1];
    return pg ? `${pg.w} / ${pg.h}` : '210 / 297';
  }

  text(p: number): string {
    return this.manifest?.pages?.[p - 1]?.text || '';
  }

  private load(p: number, size: 'm' | 'l' = 'm'): void {
    const key = `${p}:${size}`;
    if (this.urls.has(key) || this.pending.has(key)) return;
    this.pending.add(key);
    this.guides.pageImage(this.slug, p, size).subscribe({
      next: (u) => {
        this.urls.set(key, u);
        // Object URLs this reader created from the API's own image bytes.
        this.safe.set(key, this.sanitizer.bypassSecurityTrustUrl(u));
        this.pending.delete(key);
      },
      error: () => this.pending.delete(key),
    });
  }

  // ------------------------------------------------------------ states

  signIn(): void {
    this.session.setSuccessRoute(guideReaderPath(this.slug));
    this.router.navigateByUrl('/login');
  }
}
